'use client';

import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { AdornedInput, Alert, Button, FormField, FormHelp, Input, Label, Paragraph } from '@zillow/constellation-10';
import { HpAuthFlow } from '@zg-rentals/ts-hotpads-api-client';
import type { HpOtpSource } from '@zg-rentals/ts-hotpads-api-client';
import { css } from '@/styled-system/css';
import { getPathname } from '@/src/i18n/navigation';
import type { SupportedLocale } from '@/src/i18n/routing';
import { v3ErrorReason } from '@/src/lib/api/client/hotpadsApiV3Client';
import { GatedButton } from '@/src/components/ui/GatedButton';
import { isValidEmail } from '@/src/lib/auth/email';
import { navigateToExternalAuth } from '@/src/lib/auth/provider-navigation';
import { isSafeRedirect } from '@/src/lib/auth/safe-redirect';
import type { AuthMessageKey } from '@/src/lib/auth/status';
import { mapAuthStatusToMessageKey } from '@/src/lib/auth/status';
import { SEND_BLOCKED_REASONS, initialSendErrorKey } from '@/src/lib/hooks/auth/otpReasons';
import { useAuthFlow, useEmailPasswordLogin, useSendOtp } from '@/src/lib/hooks/query/useUserQuery';
import { AppleSignInButton } from '@/src/components/features/auth/AppleSignInButton';
import { GoogleSignInButton } from '@/src/components/features/auth/GoogleSignInButton';
import { OtpSignInForm } from '@/src/components/features/auth/OtpSignIn/OtpSignInForm';
import { form } from '@/src/components/features/auth/login/login.styles';

type LoginStep = 'email' | 'password' | 'otp';

type CodeEntryStep = Exclude<LoginStep, 'otp'>;

type LoginFormProps = {
  callbackErrorKey?: AuthMessageKey | null;
  emailFooter?: ReactNode;
  // In-page sign-ins only (email/password + OTP); caller redirects. OAuth doesn't fire this.
  onAuthenticated: (response: { loggedIn: boolean; forwardUrl?: string }) => void;
  onEmailError?: (error: unknown) => void;
  onPasswordError?: (error: unknown) => void;
  passwordFooter?: ReactNode;
  // Landing page for the navigate-away sign-ins (OAuth + SSO); they don't fire onAuthenticated.
  postLoginRedirect?: string;
  otpSource: HpOtpSource;
};

const oauthStack = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'default',
});

const separatorText = css({
  color: 'text.subtle',
  marginBlock: 'loose',
  textAlign: 'center',
  textTransform: 'uppercase',
});

function buildSsoCallbackPath(locale: string, postLoginRedirect?: string): string {
  const loginPath = getPathname({ href: '/login', locale: locale as SupportedLocale });

  return postLoginRedirect ? `${loginPath}?redirect=${encodeURIComponent(postLoginRedirect)}` : loginPath;
}

export function LoginForm({
  callbackErrorKey,
  emailFooter,
  onAuthenticated,
  onEmailError,
  onPasswordError,
  passwordFooter,
  postLoginRedirect,
  otpSource,
}: LoginFormProps) {
  const locale = useLocale();
  const t = useTranslations('auth');
  const authFlowMutation = useAuthFlow();
  const passwordMutation = useEmailPasswordLogin();
  const sendOtpMutation = useSendOtp();
  const [step, setStep] = useState<LoginStep>('email');
  const [otpBackStep, setOtpBackStep] = useState<CodeEntryStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [otpSendLimitReached, setOtpSendLimitReached] = useState(false);

  const sendCode = (targetEmail: string, backStep: CodeEntryStep) => {
    const openCodeStep = (sendLimitReached: boolean) => {
      setOtpSendLimitReached(sendLimitReached);
      setOtpBackStep(backStep);
      setStep('otp');
    };

    sendOtpMutation.mutate(
      { postData: { email: targetEmail, source: otpSource } },
      {
        onSuccess: () => openCodeStep(false),
        onError: (mutationError) => {
          const reason = v3ErrorReason(mutationError);
          // No new code is coming, but an earlier one may still be redeemable.
          if (reason && SEND_BLOCKED_REASONS.has(reason)) {
            openCodeStep(true);
            return;
          }
          setError(t(initialSendErrorKey(reason)));
        },
      },
    );
  };

  const resolveAuthFlow = (targetEmail: string) => {
    authFlowMutation.mutate(
      { postData: { email: targetEmail, callbackPath: buildSsoCallbackPath(locale, postLoginRedirect) } },
      {
        onSuccess: (response) => {
          switch (response.data?.flow) {
            case HpAuthFlow.SSO: {
              const forwardUrl = response.data.forwardUrl;
              if (
                forwardUrl &&
                (forwardUrl.startsWith('https://') || forwardUrl.startsWith('http://') || isSafeRedirect(forwardUrl))
              ) {
                navigateToExternalAuth(forwardUrl, window.location.pathname);
                return;
              }
              setError(t('errorGeneric'));
              return;
            }
            case HpAuthFlow.DEFAULT:
              setStep('password');
              return;
            case HpAuthFlow.OTP:
              sendCode(targetEmail, 'email');
              return;
            default:
              setError(t('errorGeneric'));
          }
        },
        onError: (mutationError) => {
          onEmailError?.(mutationError);
          setError(t('errorGeneric'));
        },
      },
    );
  };

  const handleEmailSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError('');

    const trimmed = email.trim();
    if (!trimmed) {
      setError(t('errorEnterEmail'));
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError(t('errorInvalidEmail'));
      return;
    }

    setEmail(trimmed);
    resolveAuthFlow(trimmed);
  };

  const handlePasswordSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!password.trim()) {
      setError(t('errorEnterPassword'));
      return;
    }

    passwordMutation.mutate(
      { email, password },
      {
        onSuccess: (response) => {
          if (response.loggedIn) {
            onAuthenticated(response);
            return;
          }
          setError(t(mapAuthStatusToMessageKey(response.status)));
        },
        onError: (mutationError) => {
          onPasswordError?.(mutationError);
          setError(t('errorGeneric'));
        },
      },
    );
  };

  const handleChangeEmail = () => {
    setStep('email');
    setPassword('');
    setError('');
  };

  const handleSignInWithCode = () => {
    setError('');
    sendCode(email, 'password');
  };

  const isResolvingEmail = authFlowMutation.isPending || (step === 'email' && sendOtpMutation.isPending);

  const renderEmailStep = () => (
    <>
      {callbackErrorKey && (
        <Alert tone="critical" role="alert" css={{ marginBottom: 'default' }} data-testid="login-callback-error">
          {t(callbackErrorKey)}
        </Alert>
      )}
      <form className={form} onSubmit={handleEmailSubmit} noValidate>
        <FormField
          label={<Label>{t('email')}</Label>}
          error={!!error}
          description={error ? <FormHelp>{error}</FormHelp> : undefined}
          control={
            <Input
              type="email"
              placeholder={t('emailPlaceholder')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              readOnly={isResolvingEmail}
            />
          }
        />
        <GatedButton
          type="submit"
          tone="brand"
          emphasis="filled"
          size="lg"
          fluid
          disabled={isResolvingEmail || !email.trim()}
          gateReason={!isResolvingEmail && !email.trim() && t('gateHintEmail')}
        >
          {isResolvingEmail ? t('signingIn') : t('continue')}
        </GatedButton>
      </form>

      <Paragraph textStyle="body-sm" className={separatorText}>
        {t('or')}
      </Paragraph>

      <div className={oauthStack}>
        <GoogleSignInButton postLoginRedirect={postLoginRedirect} />
        <AppleSignInButton postLoginRedirect={postLoginRedirect} />
      </div>

      {emailFooter}
    </>
  );

  const renderPasswordStep = () => (
    <>
      <form className={form} onSubmit={handlePasswordSubmit} noValidate>
        <FormField
          label={<Label>{t('email')}</Label>}
          control={
            <AdornedInput
              readOnly
              input={<AdornedInput.Input value={email} tabIndex={-1} aria-label={t('email')} />}
              endAdornment={
                <AdornedInput.Adornment asChild aria-hidden={false}>
                  <Button type="button" emphasis="bare" size="sm" tone="brand" onClick={handleChangeEmail}>
                    {t('change')}
                  </Button>
                </AdornedInput.Adornment>
              }
            />
          }
        />

        <FormField
          label={<Label>{t('password')}</Label>}
          error={!!error}
          description={error ? <FormHelp>{error}</FormHelp> : undefined}
          control={
            <Input
              type="password"
              placeholder={t('passwordPlaceholder')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              autoFocus
            />
          }
        />

        <GatedButton
          type="submit"
          tone="brand"
          emphasis="filled"
          size="lg"
          fluid
          disabled={passwordMutation.isPending || !password}
          gateReason={!passwordMutation.isPending && !password && t('gateHintPassword')}
        >
          {passwordMutation.isPending ? t('signingIn') : t('continue')}
        </GatedButton>
      </form>

      <Button
        type="button"
        emphasis="outlined"
        size="lg"
        tone="brand"
        fluid
        css={{ marginTop: 'default' }}
        onClick={handleSignInWithCode}
        loading={sendOtpMutation.isPending}
      >
        {t('passwordSignInWithCode')}
      </Button>

      {passwordFooter}
    </>
  );

  const renderOtpStep = () => (
    <OtpSignInForm
      email={email}
      sendLimitReached={otpSendLimitReached}
      source={otpSource}
      onBack={() => {
        setStep(otpBackStep);
        setError('');
        setOtpSendLimitReached(false);
      }}
      onVerified={() => onAuthenticated({ loggedIn: true })}
    />
  );

  const bodyByStep: Record<LoginStep, () => ReactNode> = {
    email: renderEmailStep,
    password: renderPasswordStep,
    otp: renderOtpStep,
  };

  return bodyByStep[step]();
}
